import { Alert } from "@mui/lab";
import { Card, CardActions, CardContent, Grid } from "@mui/material";
import { StyledComponentProps } from "@mui/styles";
import * as api from "api/Knowledge";
import { FeedClassKey } from "assets/jss/common-styles";
import classNames from "classnames";
import { RequestWrapIndicator } from "components/CallServer/RequestWrapIndicator";
import MuiConfirmation from "components/MuiExtras/MuiConfirmation";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import AppMessage from "components/Translations/AppMessage";
import { ApprovalStatus, FeedType } from "models/ApiEnums";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import ActionsDefaultDropDown from "views/Timeline/Components/CardComp/ActionsDefaultDropDown";
import ActionsDefaultRight from "views/Timeline/Components/CardComp/ActionsDefaultRight";
import ActionsDraftContent from "views/Timeline/Components/CardComp/ActionsDraftContent";
import ActionsPublisher from "views/Timeline/Components/CardComp/ActionsPublisher";
import ActionsSocial from "views/Timeline/Components/CardComp/ActionsSocial";
import BodyDefault from "views/Timeline/Components/CardComp/BodyDefault";
import HeaderLine from "views/Timeline/Components/CardComp/HeaderLine";
import IconsLine from "views/Timeline/Components/CardComp/IconsLine";
import TitleLine from "views/Timeline/Components/CardComp/TitleLine";
import { RenderDefaultApprovalAction } from "views/Timeline/Components/CardFactory/CommonRenders";
import { CardCommonActions } from "views/Timeline/Components/CardLib/CardCommonActions";
import { AnswerCardSetup } from "views/Timeline/Components/CardLib/CardSetup";
import { ICardProps } from "views/Timeline/Components/CardLib/ICardProps";
import Edit from "views/Timeline/Dialogs/PublicationEdit";

/////////////////////////////////////////////////////////////////////////////////////

interface IState {
    mouseOver: boolean;
    showBody: boolean;
    dialog: any;
}

type Props = ICardProps & WithSnackbar & WithTranslation & StyledComponentProps<FeedClassKey>;

/////////////////////////////////////////////////////////////////////////////////////

class AnswerCard extends React.PureComponent<Props, IState> {

    private commonActions: CardCommonActions;

    constructor(props: Props) {
        super(props);
        this.state = {
            dialog: null,
            showBody: null,
            mouseOver: false,
        };
        this.commonActions = new CardCommonActions(props.onUpdateCard, props.snackbar.showMessage);
    }

    ///
    private processEdit = () => {
        const { item } = this.props;

        if (this.props.onBeforeEdit) {
            this.setState({ mouseOver: false });
            if (!this.props.onBeforeEdit()) return;
        }

        this.setState({
            mouseOver: false,
            dialog: (
                <Edit
                    data={item}
                    onClose={
                        (update: boolean) => {
                            if (update) {
                                this.props.onReloadPublication();
                            }
                            this.setState({ dialog: null });
                        }
                    }
                />
            )
        });
    };

    ///
    private processDelete = () => {

        this.setState({ mouseOver: false });

        new Promise<boolean>((resolve) => {
            if (this.props.onBeforeDelete) {
                this.props.onBeforeDelete()
                    .then((result) => resolve(result));
            }
            else
                resolve(false);
        }).then((handled) => {

            if (!handled) {
                this.setState({
                    dialog: (
                        <MuiConfirmation
                            title={this.props.t("Common.Feed.Answer.ConfirmationDeleteTitle")}
                            message={this.props.t("Common.Feed.Answer.ConfirmationDeleteMessage")}
                            onClose={
                                (result) => {

                                    if (result) {
                                        this.props.onRemovePublication()
                                            .then((result) => {
                                                if (result) this.props.snackbar.showMessage(this.props.t("Common.Feed.Messages.AnswerDeletedSuccess"), "success");
                                                else this.props.snackbar.showMessage(<AppMessage resource="OperationFailed" />, "error");
                                                this.setState({ dialog: null });
                                            })
                                            .catch((error) => {
                                                this.props.snackbar.showMessage(<AppMessage resource="OperationFailed" />, "error");
                                                this.setState({ dialog: null });
                                            });
                                    }
                                    else {
                                        this.setState({ dialog: null });
                                    }
                                }
                            }
                        />
                    )
                });

            }
        });
    };

    ///
    private processPublish = () => {
        const { item } = this.props;

        this.setState({
            mouseOver: false,
        });

        api.publishDraftPublication(item.publicationId, item.publicationType)
            .then(() => {
                this.props.onReloadPublication();
            });
    };

    ///
    private handleTextExpanded = (textExpanded: boolean) => {
        this.commonActions.expandText(this.props.item, textExpanded);
        this.setState({ showBody: textExpanded });
    }

    ///
    public render() {
        const {
            feedType,
            classes,
            item,
            onSidebarResetRequest,
            onUpdateCard,
            onApprovalRequest
        } = this.props;

        const { mouseOver } = this.state;

        const env = new AnswerCardSetup(this.props, this.state.showBody, this.processEdit, this.processDelete, this.processPublish, null);

        return (
            <>
                <Card
                    id={item.publicationId}
                    className={classNames(classes.card, { [classes.cardBestAnswer]: item.publication.isTheBestAnswer && (feedType == FeedType.Answers || feedType == FeedType.MyAnswers )})}
                    onMouseEnter={() => this.setState({ mouseOver: true })}
                    onMouseLeave={() => this.setState({ mouseOver: false })}
                    title={item.publication.isTheBestAnswer ? this.props.t("Common.Feed.Literals.BestAnswerText") : null}
                >
                    <RequestWrapIndicator enabled={item.isUpdating} type="update">

                        <CardContent className={classes.cardContent}>

                            {
                                // Workitem 591
                                !env.fullView &&
                                <Grid container>
                                    <Grid item xs>
                                        { /* Area 1 */}
                                        <HeaderLine
                                            classes={classes}
                                            data={item}
                                            feedType={this.props.feedType}
                                            fullView={env.fullView}
                                            inApprovalMode={env.inApprovalMode}
                                            mouseOver={mouseOver}
                                            textExpanded={env.textExpanded}
                                            topMessageMode={env.topMessageMode}
                                            onUpdateCard={onUpdateCard}
                                        />

                                        {
                                            /* Area 3 */
                                            (
                                                feedType == FeedType.List
                                                || feedType == FeedType.HistoryCommented
                                                || feedType == FeedType.HistoryLiked
                                                || feedType == FeedType.HistoryRecent
                                                || feedType == FeedType.HistoryShared
                                                || feedType == FeedType.HistoryThanks
                                                || feedType == FeedType.MyAnswers
                                                || feedType == FeedType.MyDraft
                                                || feedType == FeedType.TaskDenouncement
                                                || feedType == FeedType.TaskForYourAction
                                                || feedType == FeedType.TaskForYourApproval
                                            ) &&
                                            <TitleLine
                                                classes={classes}
                                                data={item}
                                                feedType={this.props.feedType}
                                                fullView={env.fullView}
                                                onTitleClick={env.titleHandle}
                                            />
                                        }
                                    </Grid>
                                    <Grid item>
                                        { /* Area 2 */}
                                        <IconsLine
                                            data={item}
                                            feedType={this.props.feedType}
                                            mouseOver={mouseOver}
                                            textExpanded={env.textExpanded}
                                            classes={classes}
                                            onEditPublication={env.editHandle}
                                            onHidePublication={env.visibilityHandle}
                                            onRemovePublication={env.deleteHandle}
                                            onViewLater={item.permissions.canViewLater ? this.commonActions.viewLater : null}
                                            onTextExpandedChange={this.handleTextExpanded}
                                            onCustomAction={this.props.onCustomAction}
                                        />
                                    </Grid>
                                </Grid>
                            }

                            {
                                /* Area 4, 5 e 6 */
                                env.showBodyRegion &&
                                <BodyDefault
                                    classes={classes}
                                    data={item}
                                    feedType={feedType}
                                    fullView={env.fullView}
                                    onTextExpandedChange={this.handleTextExpanded}
                                />
                            }

                            {
                                /* Area 7 */
                                env.hasAccess && !env.inApprovalMode && (env.textExpanded == null || env.textExpanded === true) &&
                                <CardActions disableSpacing className={classes.cardActions} >
                                    <Grid container
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                    >
                                        {
                                            item.publication.isDraft === true
                                                ? (
                                                    <Grid item xs>
                                                        <ActionsDraftContent
                                                            classes={classes}
                                                            data={item}
                                                            fullView={env.fullView}
                                                            onEditPublication={env.editHandle}
                                                            onDeletePublication={env.deleteHandle}
                                                            onPublishPublication={env.publishHandle}
                                                        />
                                                    </Grid>
                                                )
                                                : feedType == FeedType.MyDraft
                                                    ? (
                                                        <Grid item xs>
                                                            <Alert severity="info">{this.props.t("Common.Feed.Messages.Published")}</Alert>
                                                        </Grid>
                                                    )
                                                    : item.publication.approvalStatus == ApprovalStatus.Approved
                                                        ? (
                                                            <>
                                                                <Grid item>
                                                                    <ActionsSocial
                                                                        data={item}
                                                                        fullView={env.fullView}
                                                                        showCommentsButton={true}
                                                                        onUpdateCard={onUpdateCard}
                                                                    />
                                                                </Grid>

                                                                <Grid>
                                                                    <ActionsDefaultRight
                                                                        classes={classes}
                                                                        data={item}
                                                                        fullView={env.fullView}
                                                                        showAddToFavorites
                                                                        showShareButton
                                                                        showRelationsButton
                                                                        onUpdateCard={onUpdateCard}
                                                                        onSidebarResetRequest={onSidebarResetRequest}
                                                                    />

                                                                    <ActionsDefaultDropDown
                                                                        classes={classes}
                                                                        data={item}
                                                                        feedType={feedType}
                                                                        fullView={env.fullView}
                                                                        onEditPublication={env.editHandle}
                                                                        onRemovePublication={env.deleteHandle}
                                                                        onReloadPublication={env.reloadHandle}
                                                                        onUpdateCard={onUpdateCard}
                                                                    />
                                                                </Grid>
                                                            </>
                                                        )
                                                        : (
                                                            <ActionsPublisher
                                                                data={item}
                                                                onEditPublication={env.editHandle}
                                                                onRemovePublication={env.deleteHandle}
                                                                onPublishPublication={env.publishHandle}
                                                                removeInDropDown={(item.publication.approvalStatus == ApprovalStatus.Pending || item.publication.approvalStatus == ApprovalStatus.PendingCognitiveAnalysis) && item.publication.isDraft === false}
                                                            />
                                                        )
                                        }
                                    </Grid>
                                </CardActions>
                            }

                            {
                                /* Area 8 */
                                env.hasAccess &&
                                <>
                                    {
                                        !env.inApprovalMode && !env.isDenouncement
                                            ? null
                                            : <RenderDefaultApprovalAction
                                                classes={classes}
                                                hasBody={env.hasBody}
                                                showBody={env.showBody}
                                                item={item}
                                                onUpdateCard={onUpdateCard}
                                                onApprovalRequest={onApprovalRequest}
                                                onExpandText={this.handleTextExpanded}
                                            />
                                    }
                                </>
                            }

                        </CardContent>

                    </RequestWrapIndicator>
                </Card >

                {this.state.dialog}
            </>
        );
    }
}


export default withSnackbar(withTranslation()(AnswerCard));
